<script setup lang="ts">
import type { SidebarProps } from './index';
import { Sheet, SheetContent } from '../sheet';
import { cn } from '../ui-utils.ts';
import { SIDEBAR_WIDTH_MOBILE, useSidebar } from './utils';

defineOptions({
  inheritAttrs: false,
});

const props = withDefaults(defineProps<SidebarProps>(), {
  side: 'left',
  variant: 'sidebar',
  collapsible: 'offcanvas',
});

const { isMobile, state, openMobile, setOpenMobile } = useSidebar();
</script>

<template>
  <div
    v-if="collapsible === 'none'"
    :class="
      cn('text-sidebar-foreground sidebar-sparkles bg-brand-blue flex h-full w-(--sidebar-width) flex-col', props.class)
    "
    v-bind="$attrs"
  >
    <slot />
  </div>

  <Sheet v-else-if="isMobile" :open="openMobile" v-bind="$attrs" @update:open="setOpenMobile">
    <SheetContent
      data-sidebar="sidebar"
      data-mobile="true"
      :side="side"
      class="text-sidebar-foreground sidebar-sparkles bg-brand-blue w-(--sidebar-width) p-0 [&>button]:hidden"
      :style="{
        '--sidebar-width': SIDEBAR_WIDTH_MOBILE,
      }"
    >
      <div class="flex h-full w-full flex-col">
        <slot />
      </div>
    </SheetContent>
  </Sheet>

  <div
    v-else
    class="group peer hidden md:block"
    :data-state="state"
    :data-collapsible="state === 'collapsed' ? collapsible : ''"
    :data-variant="variant"
    :data-side="side"
  >
    <!-- This is what handles the sidebar gap on desktop  -->
    <div
      :class="
        cn(
          'relative h-svh w-(--sidebar-width) bg-transparent transition-[width] duration-200 ease-linear',
          'group-data-[collapsible=offcanvas]:w-0',
          'group-data-[side=right]:rotate-180',
          variant === 'floating' || variant === 'inset'
            ? 'group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+(--spacing(4)))]'
            : 'group-data-[collapsible=icon]:w-(--sidebar-width-icon)',
        )
      "
    />
    <div
      :class="
        cn(
          'fixed inset-y-0 z-10 hidden h-svh w-(--sidebar-width) transition-[left,right,width] duration-200 ease-linear md:flex',
          side === 'left'
            ? 'left-0 group-data-[collapsible=offcanvas]:left-[calc(var(--sidebar-width)*-1)]'
            : 'right-0 group-data-[collapsible=offcanvas]:right-[calc(var(--sidebar-width)*-1)]',
          // Adjust the padding for floating and inset variants.
          variant === 'floating' || variant === 'inset'
            ? 'p-2 group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+(--spacing(4))+2px)]'
            : 'group-data-[collapsible=icon]:w-(--sidebar-width-icon) group-data-[side=left]:border-r group-data-[side=right]:border-l',
          props.class,
        )
      "
      v-bind="$attrs"
    >
      <div
        data-sidebar="sidebar"
        class="sidebar-sparkles group-data-[variant=floating]:border-sidebar-border bg-brand-blue flex h-full w-full flex-col group-data-[variant=floating]:rounded-lg group-data-[variant=floating]:border group-data-[variant=floating]:shadow-sm"
      >
        <slot />
      </div>
    </div>
  </div>
</template>

<style scoped>
.sidebar-sparkles {
  position: relative;
  background-color: #081848 !important; /* Force brand-blue color */
  background-image:
    linear-gradient(135deg, #081848 0%, #0a1d5c 50%, #081848 100%), url('../../tri-frontend/assets/sparkles1.png'),
    url('../../tri-frontend/assets/sparkles2.png');
  background-position:
    0 0,
    15% 10%,
    /* Top left area */ 85% 15%; /* Top right area */
  background-size:
    100% 100%,
    80px 80px,
    70px 70px;
  background-repeat: no-repeat, no-repeat, no-repeat;
}

.sidebar-sparkles::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: url('../../tri-frontend/assets/sparkles2.png'), url('../../tri-frontend/assets/sparkles1.png');
  background-position:
    20% 95%,
    /* Footer left area */ 89% 99%; /* Footer right area */
  background-size:
    60px 60px,
    75px 75px;
  background-repeat: no-repeat;
  opacity: 0.4;
  pointer-events: none;
}

.sidebar-sparkles::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: url('../../tri-frontend/assets/sparkles1.png');
  background-position: 50% 5%; /* Top center area */
  background-size: 65px 65px;
  background-repeat: no-repeat;
  opacity: 0.3;
  pointer-events: none;
}
</style>
